// Session guard -- what happens when the stored login is no longer valid.
//
// A token stops being accepted by the API when the user signs out in another
// tab (signout removes THAT token server-side, the other tabs still hold it),
// signs out everywhere, changes password, or a security action invalidates
// the sessions. Before this guard, the other tabs kept polling (Email Inbox
// badge, Trash, cart...) with the dead token: a flood of 401s and console
// errors, and a UI that still looked signed in.
//
// Like roleSwitchFetch.js (there is no central fetch wrapper), this wraps
// window.fetch ONCE at bootstrap (src/index.js). It never changes a request
// or a response; it only watches for a 401 on an Elakay API call that carried
// the CURRENTLY stored token, confirms once with GET /readMe that the token is
// really rejected (a 401 can also be a wrong password check), then clears the
// stale session and reloads as a visitor -- exactly what signout() does.
// It also follows a sign-out / account change made in another tab.
import { elakay_API } from "../../config";
import { clearPersistedSelectedRole } from "./index";

export const SESSION_STORAGE_KEY = "elakayUser";

const tokenOf = (raw) => {
  try { return (raw ? JSON.parse(raw) : null)?.token || null; } catch { return null; }
};
export const storedToken = () => {
  try { return tokenOf(window.localStorage.getItem(SESSION_STORAGE_KEY)); } catch { return null; }
};

const bearerOf = (input, init = {}) => {
  try {
    const requestHeaders = typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined;
    const value = new Headers(init.headers !== undefined ? init.headers : requestHeaders).get("Authorization") || "";
    return value.startsWith("Bearer ") ? value.slice(7) : null;
  } catch { return null; }
};

const urlOf = (input) => (typeof input === "string" ? input : input?.url || "");

export const installSessionGuard = ({ reload = () => window.location.reload() } = {}) => {
  if (typeof window === "undefined" || !window.fetch) return;
  if (window.__elakaySessionGuardInstalled) return;
  window.__elakaySessionGuardInstalled = true;

  const originalFetch = window.fetch.bind(window);
  let verifying = null;
  let expired = false;

  const expire = () => {
    if (expired) return;
    expired = true;
    try { window.localStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* storage blocked */ }
    clearPersistedSelectedRole();
    window.dispatchEvent(new CustomEvent("elakay:session-expired"));
    reload();
  };

  // One confirmation at a time, however many pollers got a 401 together.
  const confirmRejected = (token) => {
    if (!verifying) {
      verifying = originalFetch(`${elakay_API}/readMe`, { method: "GET", headers: { Accept: "application/json", Authorization: `Bearer ${token}` } })
        .then((response) => response.status === 401)
        .catch(() => false) // network down: never sign anyone out
        .finally(() => { verifying = null; });
    }
    return verifying;
  };

  window.fetch = (input, init) => {
    const pending = originalFetch(input, init);
    pending.then((response) => {
      if (expired || response?.status !== 401) return;
      const url = urlOf(input);
      if (!url.startsWith(elakay_API)) return;
      const token = bearerOf(input, init || {});
      if (!token || token !== storedToken()) return; // an old/foreign token: not this session
      confirmRejected(token).then((rejected) => {
        if (rejected && storedToken() === token) expire();
      });
    }, () => {});
    return pending;
  };

  // Another tab signed out or switched account: this tab's token is gone.
  // (elakayUser is also rewritten for activity timestamps: only a CHANGED
  // token counts.)
  window.addEventListener("storage", (event) => {
    if (event.key !== SESSION_STORAGE_KEY && event.key !== null) return;
    const before = event.key === null ? null : tokenOf(event.oldValue);
    const now = storedToken();
    if ((before && before !== now) || (event.key === null && !now)) {
      if (expired) return;
      expired = true;
      reload();
    }
  });
};
